import { useEffect, useMemo, useState } from 'react'

import { Button } from '@modules/shadcn/ui/Button/Button'
import { CheckBox } from '@modules/shadcn/ui/CheckBox/CheckBox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@modules/shadcn/ui/Dialog/Dialog'
import { Input } from '@modules/shadcn/ui/Input/Input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@modules/shadcn/ui/Select/Select'

import type { SubArea } from '@ai-color-modules/simulation/types'

import { BASE_AREAS } from '../utils/processParameters'

// 「統合しない」を表す Select の番兵値（空文字は Radix Select で使えないため）
const NO_BASE_MERGE = '__none__'

export interface CustomAreaSettingSavePayload {
  label: string
  useDetectedAreaOnly: boolean
  mergeTargets: number[]
  /** 統合先ベースエリアの日本語ラベル（issue #2224）。未指定なら統合しない。 */
  mergeIntoBaseArea: string | null
}

interface CustomAreaSettingDialogProps {
  open: boolean
  subAreas: SubArea[]
  targetIndex: number | null
  onOpenChange: (open: boolean) => void
  onSave: (payload: CustomAreaSettingSavePayload) => void
}

/**
 * カスタムエリア設定ダイアログ
 */
export const CustomAreaSettingDialog: React.FC<CustomAreaSettingDialogProps> = ({
  open,
  subAreas,
  targetIndex,
  onOpenChange,
  onSave,
}) => {
  const target = targetIndex !== null ? subAreas[targetIndex] : undefined

  const [label, setLabel] = useState('')
  const [useDetectedAreaOnly, setUseDetectedAreaOnly] = useState(true)
  const [mergeTargets, setMergeTargets] = useState<number[]>([])
  // 統合先ベースエリア（issue #2224）。NO_BASE_MERGE は「統合しない」。
  const [mergeIntoBaseArea, setMergeIntoBaseArea] = useState<string>(NO_BASE_MERGE)

  useEffect(() => {
    if (target) {
      setLabel(target.label ?? `カスタムエリア${(targetIndex ?? 0) + 1}`)
      setUseDetectedAreaOnly(target.useDetectedAreaOnly ?? true)
      setMergeTargets([])
      setMergeIntoBaseArea(NO_BASE_MERGE)
    }
  }, [target, targetIndex])

  // ベースエリアに統合する場合、カスタムエリア同士の結合は同時に行わない（排他）。
  const isBaseMerge = mergeIntoBaseArea !== NO_BASE_MERGE

  const mergeCandidates = useMemo(() => {
    if (targetIndex === null) return []
    return subAreas
      .map((area, index) => ({
        index,
        label: area.label ?? `カスタムエリア${index + 1}`,
      }))
      .filter(area => area.index !== targetIndex)
  }, [subAreas, targetIndex])

  if (!target) {
    return null
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>カスタムエリア設定</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <section className="space-y-2">
            <p className="text-sm font-medium text-slate-900">ラベル</p>
            <Input
              aria-label="ラベル"
              value={label}
              onChange={event => setLabel(event.target.value)}
            />
          </section>

          <section className="space-y-2">
            <p className="text-sm font-medium text-slate-900">選択範囲</p>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <CheckBox
                checked={useDetectedAreaOnly}
                id="detected-only"
                onCheckedChange={(checked: boolean) => setUseDetectedAreaOnly(checked === true)}
              />
              <span>カスタムエリアの有効範囲は、ベースエリアの選択範囲内に限定します。</span>
            </label>
            {/* issue #2413 の補足文は、塗り方オプション全体の整理（#2541）にまとめるため
                リリースを見送り（2026-09-15 PdM 判断）。再開時は下記と import を戻す。
            <p className="pl-6 text-xs text-slate-600">{CUSTOM_AREA_DETECTED_ONLY_DESCRIPTION}</p> */}
          </section>

          <section className="space-y-2">
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-900">ベースエリアに統合</p>
              <p className="text-xs text-slate-600">
                このカスタムエリアを選択したベースエリアの一部として扱い、色・素材の設定をベースエリアと同じにします。統合を解除したい場合は、再度カスタムエリアとして設定してください。
              </p>
            </div>
            <Select onValueChange={setMergeIntoBaseArea} value={mergeIntoBaseArea}>
              <SelectTrigger aria-label="統合先ベースエリア">
                <SelectValue placeholder="統合しない" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_BASE_MERGE}>統合しない</SelectItem>
                {BASE_AREAS.map(areaLabel => (
                  <SelectItem key={areaLabel} value={areaLabel}>
                    {areaLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          <section className="space-y-3">
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-900">カスタムエリアの結合</p>
              <p className="text-xs text-slate-600">
                選択範囲を結合したいカスタムエリアをすべて選択してください。保存後に結合を取り消すことはできません。
              </p>
            </div>
            {isBaseMerge ? (
              <p className="text-xs text-slate-500">
                ベースエリアに統合する場合、カスタムエリア同士の結合は行えません。
              </p>
            ) : mergeCandidates.length > 0 ? (
              <div className="space-y-2">
                {mergeCandidates.map(candidate => {
                  const checked = mergeTargets.includes(candidate.index)
                  return (
                    <label
                      className="flex items-center gap-2 text-sm text-slate-700"
                      key={candidate.index}
                    >
                      <CheckBox
                        checked={checked}
                        id={`merge-${candidate.index}`}
                        onCheckedChange={(next: boolean) => {
                          setMergeTargets(prev => {
                            if (next === true) {
                              return [...prev, candidate.index]
                            }
                            return prev.filter(idx => idx !== candidate.index)
                          })
                        }}
                      />
                      <span>{candidate.label}</span>
                    </label>
                  )
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-500">結合できるカスタムエリアはありません</p>
            )}
          </section>
        </div>
        <DialogFooter className="gap-2 sm:justify-end">
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            キャンセル
          </Button>
          <Button
            onClick={() => {
              onSave({
                label: label.trim(),
                useDetectedAreaOnly,
                // ベースエリア統合時はカスタム同士の結合を送らない（排他）
                mergeTargets: isBaseMerge ? [] : mergeTargets,
                mergeIntoBaseArea: isBaseMerge ? mergeIntoBaseArea : null,
              })
            }}
            type="button"
          >
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
